// ════════════════════════════════════════════════════════
//  BOTS PAGE — Admin paneldan Telegram botlarni boshqarish
// ════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from 'react'
import { botsApi, authApi, resolveAvatarUrl } from '../api'
import { Card, Modal, ProgressBar, ConfirmDeleteModal } from '../components/UI'
import { useAuthStore, useStore } from '../store'
import {
  HiCheck, HiMinusCircle, HiCheckCircle, HiPlay, HiStop, HiTrash, HiCamera,
  HiChatBubbleLeftRight, HiShieldCheck, HiLockClosed
} from 'react-icons/hi2'
import { Badge, Switch, Alert, Loader } from '../ui'

export function Bots() {
  const addToast = useStore((s) => s.addToast)
  const [bots, setBots] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editBot, setEditBot] = useState(null)
  const [form, setForm] = useState({ name: '', token: '', description: '', use_webhook: false })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [deleteBot, setDeleteBot] = useState(null)

  useEffect(() => { loadBots() }, [])

  const loadBots = async () => {
    try {
      const [botsData, statusData] = await Promise.all([botsApi.list(), botsApi.status()])
      setBots(botsData)
      setStatus(statusData)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const openAdd = () => {
    setEditBot(null)
    setForm({ name: '', token: '', description: '', use_webhook: false })
    setError('')
    setModalOpen(true)
  }

  const openEdit = (bot) => {
    setEditBot(bot)
    setForm({ name: bot.name, token: '', description: bot.description || '', use_webhook: bot.use_webhook })
    setError('')
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Bot nomi talab qilinadi"); return }
    if (!editBot && !form.token.trim()) { setError("Token talab qilinadi"); return }
    setSaving(true)
    setError('')
    try {
      if (editBot) {
        const body = { name: form.name, description: form.description, use_webhook: form.use_webhook }
        if (form.token.trim()) body.token = form.token
        await botsApi.update(editBot.id, body)
      } else {
        await botsApi.add(form)
      }
      setModalOpen(false)
      await loadBots()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (bot) => {
    try {
      await botsApi.update(bot.id, { is_active: !bot.is_active })
      await loadBots()
    } catch (e) {
      addToast(e.message || "Bot holatini o'zgartirib bo'lmadi", 'critical')
    }
  }

  const handleDelete = (bot) => {
    setDeleteBot(bot)
  }

  const confirmDelete = async () => {
    if (!deleteBot) return
    try {
      await botsApi.delete(deleteBot.id)
      await loadBots()
      setDeleteBot(null)
    } catch (e) {
      addToast(e.message || "Botni o'chirib bo'lmadi", 'critical')
    }
  }

  return (
    <Card 
      title="Telegram Botlar" 
      actions={<button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={openAdd}>+ Bot qo'shish</button>}
    >
      <div className="card-body">
        {/* Status */}
        {status && (
          <div style={{ display: 'flex', gap: 24, fontSize: 13, color: 'var(--ui-text-2)', marginBottom: 24, padding: '12px 16px', background: 'var(--ui-surface-2)', borderRadius: 6, flexWrap: 'wrap' }}>
            <div><span style={{ fontWeight: 600, color: 'var(--ui-text-1)' }}>Jami botlar:</span> {status.total_bots}</div>
            <div><span style={{ fontWeight: 600, color: 'var(--ui-text-1)' }}>Faol botlar:</span> {status.running_bots}</div>
            <div><span style={{ fontWeight: 600, color: 'var(--ui-text-1)' }}>To'xtatilgan:</span> {status.total_bots - status.running_bots}</div>
            <div><span style={{ fontWeight: 600, color: 'var(--ui-text-1)' }}>Umumiy foydalanuvchilar:</span> {bots.reduce((a, b) => a + b.total_users, 0)}</div>
          </div>
        )}

      {/* Bots grid */}
      {loading ? (
        // Oddiy "Yuklanmoqda..." matni o'rniga belgi: panel yuklanmoqda — "working"
        <Loader state="working" block />
      ) : bots.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <HiChatBubbleLeftRight size={32} style={{ color: 'var(--ui-text-3)', marginBottom: 16 }} aria-hidden="true" />
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ui-text-1)', marginBottom: 8 }}>Hozircha bot yo'q</div>
          <div style={{ fontSize: 13, color: 'var(--ui-text-3)', marginBottom: 24 }}>
            Birinchi Telegram botingizni qo'shing
          </div>
          <button className="btn btn-primary" onClick={openAdd}>+ Bot qo'shish</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
          {bots.map((bot) => {
            const isRunning = status?.bots?.[bot.id]?.is_running
            return (
              <div key={bot.id} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="card-hdr">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', color: 'var(--ui-text-1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <HiChatBubbleLeftRight size={20} aria-hidden="true" />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{bot.name}</div>
                      {bot.username && <div style={{ fontSize: 11, color: 'var(--ui-text-2)', fontWeight: 500 }}>@{bot.username}</div>}
                    </div>
                  </div>
                  <Badge tone={isRunning ? 'ok' : 'neutral'}>
                    {isRunning ? 'Faol' : 'Nofaol'}
                  </Badge>
                </div>
                <div className="card-body" style={{ flex: 1 }}>
                  {bot.description && <p style={{ fontSize: 12, color: 'var(--ui-text-2)', marginBottom: 12, lineHeight: 1.6 }}>{bot.description}</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                      { k: 'Jami ticketlar', v: bot.total_tickets },
                      { k: 'Foydalanuvchilar', v: bot.total_users },
                    ].map(({ k, v }) => (
                      <div key={k} style={{ textAlign: 'center', background: 'var(--ui-surface-2)', borderRadius: 6, padding: '8px 4px' }}>
                        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--ui-text-1)', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
                        <div style={{ fontSize: 11, color: 'var(--ui-text-3)', marginTop: 4 }}>{k}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ui-text-3)', fontFamily: 'var(--ui-mono)', background: 'var(--ui-surface-2)', padding: '4px 8px', borderRadius: 6 }}>
                    Token: {bot.token_masked}
                  </div>
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ui-border)', display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }} onClick={() => openEdit(bot)}>Tahrirlash</button>
                  <button className={`btn ${bot.is_active ? 'btn-danger' : 'btn-success'} btn-sm`} onClick={() => handleToggle(bot)}>
                    {bot.is_active ? <><HiStop aria-hidden="true" /> Stop</> : <><HiPlay aria-hidden="true" /> Start</>}
                  </button>
                  <button className="btn btn-danger btn-sm btn-icon" title="O'chirish" aria-label={`Botni o'chirish: ${bot.name}`} onClick={() => handleDelete(bot)}><HiTrash aria-hidden="true" /></button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      </div>

      {/* MODALLAR */}
      {modalOpen && <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editBot ? 'Botni tahrirlash' : "Yangi bot qo'shish"}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Bekor</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saqlanmoqda...' : <><HiCheck /> Saqlash</>}
            </button>
          </>
        }
      >
        {error && <div style={{ background: 'var(--ui-st-critical-bg)', border: '1px solid var(--ui-st-critical)', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: 'var(--ui-st-critical)' }}>{error}</div>}

        <div className="form-group"><label className="form-label">Bot nomi *</label><input className="form-input" placeholder="Asosiy bot" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="form-group">
          <label className="form-label">Bot tokeni {editBot ? '(o\'zgartirish uchun kiriting)' : '*'}</label>
          <input className="form-input" type="password" placeholder="123456789:AAFxxxxxxxx" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} />
          <div style={{ fontSize: 11, color: 'var(--ui-text-3)', marginTop: 4 }}>@BotFather dan olingan token</div>
        </div>
        <div className="form-group"><label className="form-label">Tavsif</label><textarea className="form-input" rows={3} style={{ resize: 'none' }} placeholder="Bot haqida qisqacha..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--ui-surface-2)', borderRadius: 6, border: '1px solid var(--ui-border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Webhook rejimi</div>
            <div style={{ fontSize: 11, color: 'var(--ui-text-3)', marginTop: 2 }}>Production uchun tavsiya etiladi (HTTPS kerak)</div>
          </div>
          <Switch
            checked={!!form.use_webhook}
            onChange={() => setForm({ ...form, use_webhook: !form.use_webhook })}
            aria-label="Webhook rejimi"
          />
        </div>
      </Modal>}

      <ConfirmDeleteModal
        open={!!deleteBot}
        onClose={() => setDeleteBot(null)}
        onConfirm={confirmDelete}
        itemName={deleteBot?.name || ''}
      />
    </Card>
  )
}


// ════════════════════════════════════════════════════════
//  PROFILE PAGE
// ════════════════════════════════════════════════════════

export function Profile() {
  const { user, updateUser, isSuperAdmin } = useAuthStore()
  const [tab, setTab] = useState('info')

  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    department: user?.department || '',
    notification_telegram: user?.notification_telegram ?? true,
    notification_email: user?.notification_email ?? true,
  })
  const [passForm, setPassForm] = useState({ current_password: '', new_password: '', confirm: '' })
  const [qr, setQr] = useState(null)
  const [totpCode, setTotpCode] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState('ok')   // 'ok' | 'danger'
  const avatarInputRef = useRef(null)
  const [avatarBusy, setAvatarBusy] = useState(false)

  // Xabarni matni bilan birga toni ham beriladi — xato hech qachon yashil ko'rinmaydi.
  const say = (text, tone = 'ok') => { setMsg(text); setMsgTone(tone) }

  // Online/Offline holati (ilgari aniqlanmagan edi — profil "Ma'lumotlar" tabini buzardi)
  const [isOnline, setIsOnline] = useState(user?.is_online ?? false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  useEffect(() => { setIsOnline(user?.is_online ?? false) }, [user?.is_online])

  const handleToggleStatus = async () => {
    setTogglingStatus(true)
    try {
      const next = !isOnline
      const updated = await authApi.updateMe({ is_online: next })
      updateUser(updated)
      setIsOnline(updated?.is_online ?? next)
    } catch (err) { say(err.message || "Holatni o'zgartirib bo'lmadi", 'danger') }
    finally { setTogglingStatus(false) }
  }

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { say('Faqat rasm fayllari qabul qilinadi', 'danger'); return }
    setAvatarBusy(true); setMsg('')
    try {
      const updated = await authApi.uploadAvatar(file)
      updateUser(updated)
      say('Avatar yangilandi')
    } catch (err) { say(err.message || 'Avatarni yuklab bo\'lmadi', 'danger') }
    finally { setAvatarBusy(false); if (avatarInputRef.current) avatarInputRef.current.value = '' }
  }

  const handleAvatarRemove = async () => {
    setAvatarBusy(true); setMsg('')
    try {
      const updated = await authApi.deleteAvatar()
      updateUser(updated)
      say('Avatar o\'chirildi')
    } catch (err) { say(err.message || 'Avatarni o\'chirib bo\'lmadi', 'danger') }
    finally { setAvatarBusy(false) }
  }

  const handleSaveInfo = async () => {
    setSaving(true)
    setMsg('')
    try {
      const updated = await authApi.updateMe(form)
      updateUser(updated)
      say('Ma\'lumotlar saqlandi')
    } catch (e) { say(e.message || 'Ma\'lumotlarni saqlab bo\'lmadi', 'danger') } finally { setSaving(false) }
  }

  const handleChangePass = async () => {
    if (passForm.new_password !== passForm.confirm) { say('Yangi parollar mos kelmaydi', 'danger'); return }
    setSaving(true)
    try {
      await authApi.changePassword({ current_password: passForm.current_password, new_password: passForm.new_password })
      say('Parol o\'zgartirildi')
      setPassForm({ current_password: '', new_password: '', confirm: '' })
    } catch (e) { say(e.message || 'Parolni o\'zgartirib bo\'lmadi', 'danger') } finally { setSaving(false) }
  }

  const handleEnable2FA = async () => {
    try {
      const data = await authApi.enable2FA()
      setQr(data)
    } catch (e) { say(e.message || '2FA ni yoqib bo\'lmadi', 'danger') }
  }

  const handleVerify2FA = async () => {
    try {
      await authApi.verify2FA(totpCode)
      updateUser({ totp_enabled: true })
      say('2FA yoqildi')
      setQr(null)
    } catch (e) { say(e.message || 'Kod tasdiqlanmadi', 'danger') }
  }

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <h2>Mening profilim</h2>
          <p>{user?.full_name} · {user?.role === 'super_admin' ? 'Super Admin' : 'Operator'}</p>
        </div>
      </div>

      <div className="g-2-1">
        {/* LEFT: Profile card */}
        <div>
          {/* Tabs */}
          <div className="tabs">
            {[
              { id: 'info', label: 'Ma\'lumotlar' },
              { id: 'password', label: 'Parol' },
              ...(isSuperAdmin() ? [{ id: '2fa', label: '2FA' }] : []),
            ].map((t) => (
              <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => { setTab(t.id); setMsg('') }}>
                {t.label}
              </button>
            ))}
          </div>

          {msg && (
            <Alert tone={msgTone} onClose={() => setMsg('')} style={{ marginBottom: 16 }}>
              {msg}
            </Alert>
          )}

          {tab === 'info' && (
            <Card>
              <div className="card-body">
                {/* Avatar (task 4) */}
                <div style={{ display:'flex', alignItems:'center', gap:16, paddingBottom:16, marginBottom:16, borderBottom:'1px solid var(--ui-border)' }}>
                  <div style={{ position:'relative' }}>
                    {resolveAvatarUrl(user?.avatar_url)
                      ? <img src={resolveAvatarUrl(user?.avatar_url)} alt="avatar" style={{ width:72, height:72, borderRadius:'99px', objectFit:'cover', border:'1px solid var(--ui-border)' }} />
                      : <div style={{ width:72, height:72, borderRadius:'99px', background:'var(--ui-surface-2)', border:'1px solid var(--ui-border)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--ui-text-1)', fontSize:28, fontWeight:600 }}>
                          {(user?.full_name || 'U').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                        </div>}
                    <button type="button" title="Rasm yuklash" aria-label="Profil rasmini yuklash" onClick={() => avatarInputRef.current?.click()}
                      style={{ position:'absolute', bottom:0, right:0, width:26, height:26, borderRadius:'99px', border:'2px solid var(--ui-surface)', background:'var(--ui-text-1)', color:'var(--ui-surface)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                      <HiCamera size={13} aria-hidden="true" />
                    </button>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600 }}>Profil rasmi</div>
                    <div style={{ fontSize:12, color:'var(--ui-text-3)', marginTop:2 }}>JPG/PNG · kvadrat shaklda kesiladi</div>
                    <div style={{ display:'flex', gap:8, marginTop:8 }}>
                      <button className="btn btn-secondary btn-sm" disabled={avatarBusy} onClick={() => avatarInputRef.current?.click()}>
                        {avatarBusy ? '...' : 'Rasm tanlash'}
                      </button>
                      {user?.avatar_url && (
                        <button className="btn btn-danger btn-sm" disabled={avatarBusy} onClick={handleAvatarRemove}>O'chirish</button>
                      )}
                    </div>
                  </div>
                  <input ref={avatarInputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatarUpload} />
                </div>
                {/* Online/Offline toggle */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:'var(--ui-surface-2)',border:`1px solid ${isOnline ? 'var(--ui-st-ok)' : 'var(--ui-border)'}`,borderRadius:8,marginBottom:16,cursor:'pointer',transition:'border-color var(--ui-dur) var(--ui-ease)'}}
                  onClick={handleToggleStatus}>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:40,height:40,borderRadius:6,background:'var(--ui-surface-3)',color:isOnline ? 'var(--ui-st-ok)' : 'var(--ui-text-3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {isOnline ? <HiCheckCircle size={20} aria-hidden="true" /> : <HiMinusCircle size={20} aria-hidden="true" />}
                    </div>
                    <div>
                      <div style={{fontSize:14,fontWeight:600,color:isOnline ? 'var(--ui-st-ok)' : 'var(--ui-text-2)'}}>
                        {isOnline ? 'Onlayn — Murojaatlar qabul qilinmoqda' : 'Offlayn — Murojaatlar qabul qilinmaydi'}
                      </div>
                      <div style={{fontSize:12,color:'var(--ui-text-3)',marginTop:2}}>
                        {isOnline ? 'Yangi murojaatlar sizga tayinlanadi' : "Boshqa operatorlarga yo'naltiriladi"}
                      </div>
                    </div>
                  </div>
                  <button className={`btn ${isOnline ? 'btn-danger' : 'btn-success'} btn-sm`} disabled={togglingStatus}
                    onClick={e => { e.stopPropagation(); handleToggleStatus() }}>
                    {togglingStatus ? '...' : isOnline ? <><HiStop aria-hidden="true" /> Offlayn</> : <><HiPlay aria-hidden="true" /> Onlayn</>}
                  </button>
                </div>
                <div className="g2">
                  <div className="form-group"><label className="form-label">Ism Familiya</label><input className="form-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div className="g2">
                  <div className="form-group"><label className="form-label">Telefon</label><input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+998901234567" /></div>
                  <div className="form-group"><label className="form-label">Bo'lim</label><input className="form-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                </div>
                <div className="form-group"><label className="form-label">Bio</label><textarea className="form-input" rows={3} style={{ resize: 'none' }} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>

                <div style={{ borderTop: '1px solid var(--ui-border)', paddingTop: 16, marginTop: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Bildirishnomalar</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { key: 'notification_telegram', label: 'Telegram orqali', sub: 'Murojaat yangilanishlarida' },
                      { key: 'notification_email', label: 'Email orqali', sub: 'Kunlik xulosa hisobotlari' },
                    ].map(({ key, label, sub }) => (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div><div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div><div style={{ fontSize: 11, color: 'var(--ui-text-3)' }}>{sub}</div></div>
                        <Switch
                          checked={!!form[key]}
                          onChange={() => setForm({ ...form, [key]: !form[key] })}
                          aria-label={`Bildirishnoma — ${label}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <button className="btn btn-primary" style={{ marginTop: 24, width: '100%', justifyContent: 'center' }} onClick={handleSaveInfo} disabled={saving}>
                  {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                </button>
              </div>
            </Card>
          )}

          {tab === 'password' && (
            <Card>
              <div className="card-body">
                <div className="form-group"><label className="form-label">Joriy parol</label><input className="form-input" type="password" value={passForm.current_password} onChange={(e) => setPassForm({ ...passForm, current_password: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Yangi parol (min 12 belgi, katta harf va raqam)</label><input className="form-input" type="password" value={passForm.new_password} onChange={(e) => setPassForm({ ...passForm, new_password: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Yangi parolni tasdiqlang</label><input className="form-input" type="password" value={passForm.confirm} onChange={(e) => setPassForm({ ...passForm, confirm: e.target.value })} /></div>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={handleChangePass} disabled={saving}>
                  {saving ? '...' : 'Parolni o\'zgartirish'}
                </button>
              </div>
            </Card>
          )}

          {tab === '2fa' && (
            <Card>
              <div className="card-body">
                {user?.totp_enabled ? (
                  <div style={{ textAlign: 'center' }}>
                    <HiShieldCheck size={32} style={{ color: 'var(--ui-st-ok)', marginBottom: 12 }} aria-hidden="true" />
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--ui-st-ok)', marginBottom: 8 }}>2FA Yoqilgan</div>
                    <p style={{ fontSize: 13, color: 'var(--ui-text-2)', marginBottom: 24 }}>
                      Hisobingiz ikki bosqichli autentifikatsiya bilan himoyalangan.
                    </p>
                    <button className="btn btn-danger" style={{ width: '100%', justifyContent: 'center' }}>2FA ni o'chirish</button>
                  </div>
                ) : qr ? (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 13, color: 'var(--ui-text-2)', marginBottom: 16 }}>
                      Google Authenticator ilovasida QR kodni skanerlang:
                    </p>
                    {qr.qr_code_base64 ? (
                      // QR skanerlash uchun majburiy oq fon — mavzuga bog'liq emas.
                      // Kamera qora modullarni oq fonda o'qiydi; tungi mavzuda ham oq qoladi.
                      <img src={`data:${qr.qr_mime || 'image/png'};base64,${qr.qr_code_base64}`} alt="QR" style={{ width: 180, height: 180, borderRadius: 8, border: '1px solid var(--ui-border)', background: '#fff', padding: 8 }} />
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--ui-text-2)', padding: '24px 0' }}>
                        QR kod yaratilmadi — pastdagi kalitni ilovaga QO'LDA kiriting.
                      </div>
                    )}

                    {/* Zaxira yo'l: QR skanerlanmasa — kalitni qo'lda kiritish */}
                    <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', borderRadius: 6, textAlign: 'left' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 8 }}>
                        Maxfiy kalit (QR ishlamasa yoki zaxira uchun saqlang):
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <code style={{ flex: 1, fontSize: 12, wordBreak: 'break-all', fontFamily: 'var(--ui-mono)', userSelect: 'all' }}>{qr.secret}</code>
                        <button type="button" className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(qr.secret); say('Kalit nusxalandi') }}>Nusxa</button>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ui-text-2)', marginTop: 8, lineHeight: 1.5 }}>
                        Ilovada: "Enter a setup key" / "Kalitni qo'lda kiritish" → hisob nomi ixtiyoriy, kalit — yuqoridagi matn.
                      </div>
                    </div>
                    <div style={{ margin: '16px 0' }}>
                      <label className="form-label" htmlFor="totp-code">6 xonali kodni kiriting:</label>
                      <input id="totp-code" className="form-input" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="000000" style={{ textAlign:'center', fontSize:18, fontFamily:'var(--ui-mono)', fontVariantNumeric:'tabular-nums', letterSpacing:6, marginTop:8 }} maxLength={6} />
                    </div>
                    <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={handleVerify2FA}><HiCheck aria-hidden="true" /> Tasdiqlash va yoqish</button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <HiLockClosed size={32} style={{ color: 'var(--ui-text-3)', marginBottom: 12 }} aria-hidden="true" />
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>2FA ni yoqing</div>
                    <p style={{ fontSize: 13, color: 'var(--ui-text-2)', marginBottom: 24 }}>
                      Google Authenticator yoki shunga o'xshash TOTP ilova orqali hisobingizni himoyalang.
                    </p>
                    <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={handleEnable2FA}>2FA yoqish</button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT: Stats */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Faoliyat statistikasi">
            <div className="card-body">
              {[
                { label: 'Jami murojaatlar', val: user?.total_tickets || 0 },
                { label: 'Yopilgan murojaatlar', val: user?.closed_tickets || 0 },
                { label: "O'rt. javob vaqti", val: `${user?.avg_response_hours || 0}s` },
              ].map(({ label, val }) => (
                <div key={label} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: 'var(--ui-text-2)', fontWeight: 500 }}>{label}</span>
                    <span style={{ fontWeight: 600, color: 'var(--ui-text-1)', fontVariantNumeric: 'tabular-nums' }}>{val}</span>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: 'var(--ui-text-2)', fontWeight: 500 }}>Mamnuniyat reytingi</span>
                  <span style={{ fontWeight: 600, color: 'var(--ui-st-ok)', fontVariantNumeric: 'tabular-nums' }}>{user?.satisfaction_avg?.toFixed(1) || '—'}</span>
                </div>
                <ProgressBar value={(user?.satisfaction_avg / 5) * 100 || 0} tone="ok" />
              </div>
            </div>
          </Card>

          <Card title="Profil ma'lumotlari">
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { k: 'Username', v: `@${user?.username}` },
                  { k: 'Rol', v: user?.role === 'super_admin' ? 'Super Admin' : 'Operator' },
                  { k: 'Holat', v: user?.is_active ? 'Faol' : 'Bloklangan' },
                  { k: 'Max ticket', v: user?.max_tickets },
                  { k: "Ro'yxatdan o'tgan", v: user?.created_at?.slice(0, 10) },
                ].map(({ k, v }) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: 'var(--ui-text-3)' }}>{k}</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
